import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { LoadingService } from '@core/services/loading.service';
import { ThemeService } from '@core/services/theme.service';
import { TranslateService } from '@core/services/translate.service';
import { ToastService } from '@core/services/toast.service';
import type { ToastConfig } from '@shared/ui/toast';
import { Observable, Subscription, filter } from 'rxjs';

@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>
    <app-loading *ngIf="isLoading" [message]="loadingMessage"></app-loading>
    <ng-container *ngIf="toasts$ | async as toasts">
      <div class="app-toast-zone app-toast-zone--top-left">
        <app-toast *ngFor="let toast of filterToasts(toasts, 'top-left'); trackBy: trackToast" [title]="toast.title" [message]="toast.message" [tone]="toast.tone!" [position]="toast.position!" [isProcess]="toast.isProcess!" [isRemoving]="toast.isRemoving!" [showLeftIcon]="toast.showLeftIcon!" [showHeader]="toast.showHeader!" [showMessage]="toast.showMessage!" (closeToast)="removeToast(toast.id!)"></app-toast>
      </div>
      <div class="app-toast-zone app-toast-zone--top-center">
        <app-toast *ngFor="let toast of filterToasts(toasts, 'top-center'); trackBy: trackToast" [title]="toast.title" [message]="toast.message" [tone]="toast.tone!" [position]="toast.position!" [isProcess]="toast.isProcess!" [isRemoving]="toast.isRemoving!" [showLeftIcon]="toast.showLeftIcon!" [showHeader]="toast.showHeader!" [showMessage]="toast.showMessage!" (closeToast)="removeToast(toast.id!)"></app-toast>
      </div>
      <div class="app-toast-zone app-toast-zone--top-right">
        <app-toast *ngFor="let toast of filterToasts(toasts, 'top-right'); trackBy: trackToast" [title]="toast.title" [message]="toast.message" [tone]="toast.tone!" [position]="toast.position!" [isProcess]="toast.isProcess!" [isRemoving]="toast.isRemoving!" [showLeftIcon]="toast.showLeftIcon!" [showHeader]="toast.showHeader!" [showMessage]="toast.showMessage!" (closeToast)="removeToast(toast.id!)"></app-toast>
      </div>
      <div class="app-toast-zone app-toast-zone--bottom-left">
        <app-toast *ngFor="let toast of filterToasts(toasts, 'bottom-left'); trackBy: trackToast" [title]="toast.title" [message]="toast.message" [tone]="toast.tone!" [position]="toast.position!" [isProcess]="toast.isProcess!" [isRemoving]="toast.isRemoving!" [showLeftIcon]="toast.showLeftIcon!" [showHeader]="toast.showHeader!" [showMessage]="toast.showMessage!" (closeToast)="removeToast(toast.id!)"></app-toast>
      </div>
      <div class="app-toast-zone app-toast-zone--bottom-center">
        <app-toast *ngFor="let toast of filterToasts(toasts, 'bottom-center'); trackBy: trackToast" [title]="toast.title" [message]="toast.message" [tone]="toast.tone!" [position]="toast.position!" [isProcess]="toast.isProcess!" [isRemoving]="toast.isRemoving!" [showLeftIcon]="toast.showLeftIcon!" [showHeader]="toast.showHeader!" [showMessage]="toast.showMessage!" (closeToast)="removeToast(toast.id!)"></app-toast>
      </div>
      <div class="app-toast-zone app-toast-zone--bottom-right">
        <app-toast *ngFor="let toast of filterToasts(toasts, 'bottom-right'); trackBy: trackToast" [title]="toast.title" [message]="toast.message" [tone]="toast.tone!" [position]="toast.position!" [isProcess]="toast.isProcess!" [isRemoving]="toast.isRemoving!" [showLeftIcon]="toast.showLeftIcon!" [showHeader]="toast.showHeader!" [showMessage]="toast.showMessage!" (closeToast)="removeToast(toast.id!)"></app-toast>
      </div>
    </ng-container>
  `,
  styles: [`
    .app-toast-zone {
      position: fixed;
      z-index: 9999;
      display: flex;
      gap: 16px;
      pointer-events: none;
    }
    .app-toast-zone--top-left { top: 24px; left: 24px; flex-direction: column; }
    .app-toast-zone--top-center { top: 24px; left: 50%; transform: translateX(-50%); flex-direction: column; }
    .app-toast-zone--top-right { top: 24px; right: 24px; flex-direction: column; }
    .app-toast-zone--bottom-left { bottom: 24px; left: 24px; flex-direction: column-reverse; }
    .app-toast-zone--bottom-center { bottom: 24px; left: 50%; transform: translateX(-50%); flex-direction: column-reverse; }
    .app-toast-zone--bottom-right { bottom: 24px; right: 24px; flex-direction: column-reverse; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading: boolean = false;
  loadingMessage: string | null = null;
  toasts$: Observable<ToastConfig[]>;
  private readonly subscriptions = new Subscription();

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private router: Router,
    private loadingService: LoadingService,
    private themeService: ThemeService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService
  ) {
    this.toasts$ = this.toastService.toasts$;
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.loadingService.loading$.subscribe((loading) => {
        this.isLoading = loading;
        this.detectChangesSafely();
      })
    );

    this.subscriptions.add(
      this.loadingService.message$.subscribe((message) => {
        this.loadingMessage = message;
        this.detectChangesSafely();
      })
    );

    this.themeService.getTheme();

    this.subscriptions.add(
      this.translateService.getLanguageChanged$().subscribe(() => {
        const appName = this.translateService.instant('APP_NAME');
        this.titleService.setTitle(appName);
        this.metaService.updateTag({ name: 'apple-mobile-web-app-title', content: appName });
        this.metaService.updateTag({ name: 'application-name', content: appName });
        this.cdr.markForCheck();
      })
    );
    
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        window.scrollTo(0, 0);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private detectChangesSafely(): void {
    if (!(this.cdr as any)['destroyed']) {
      this.cdr.detectChanges();
    }
  }

  removeToast(id: string): void {
    this.toastService.removeToast(id);
  }

  filterToasts(toasts: ToastConfig[], position: string): ToastConfig[] {
    return toasts.filter(t => t.position === position);
  }

  trackToast(_index: number, toast: ToastConfig): string {
    return toast.id;
  }
}
